import React, { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import * as FiIcons from 'react-icons/fi'
import type { IconType } from 'react-icons'

export interface ReactIconPickerProps {
  value: string
  onChange: (iconName: string) => void
  size?: number
  placeholder?: string
}

const FI_ICON_CATEGORIES: Record<string, string[]> = {
  'Project & Work': [
    'FiFolder', 'FiFolderPlus', 'FiFolderMinus', 'FiFile', 'FiFileText',
    'FiFileMinus', 'FiFilePlus', 'FiClipboard', 'FiBookmark', 'FiBook', 'FiBookOpen',
    'FiArchive', 'FiBriefcase', 'FiAward', 'FiFlag', 'FiTarget', 'FiTrello',
    'FiLayout', 'FiGrid', 'FiColumns', 'FiList', 'FiCheckSquare', 'FiCalendar',
  ],
  'Technology': [
    'FiMonitor', 'FiSmartphone', 'FiTablet', 'FiServer', 'FiDatabase', 'FiHardDrive',
    'FiCpu', 'FiPrinter', 'FiWifi', 'FiWifiOff', 'FiCloud', 'FiCloudOff',
    'FiCode', 'FiTerminal', 'FiGitBranch', 'FiGitCommit', 'FiGitMerge',
    'FiGitPullRequest', 'FiGlobe', 'FiPackage', 'FiBox', 'FiLayers', 'FiCodesandbox',
  ],
  'Communication': [
    'FiMail', 'FiInbox', 'FiSend', 'FiMessageSquare', 'FiMessageCircle',
    'FiBell', 'FiBellOff', 'FiPhone', 'FiPhoneCall', 'FiPhoneMissed',
    'FiPhoneOff', 'FiVideo', 'FiVideoOff', 'FiMic', 'FiMicOff',
    'FiRadio', 'FiRss', 'FiShare', 'FiShare2', 'FiAtSign', 'FiHash', 'FiLink', 'FiLink2',
  ],
  'Users & People': [
    'FiUser', 'FiUsers', 'FiUserCheck', 'FiUserMinus', 'FiUserPlus', 'FiUserX',
    'FiHeart', 'FiSmile', 'FiFrown', 'FiMeh', 'FiThumbsUp', 'FiThumbsDown',
  ],
  'Business & Finance': [
    'FiDollarSign', 'FiCreditCard', 'FiShoppingCart', 'FiShoppingBag', 'FiTag',
    'FiGift', 'FiPercent', 'FiTrendingUp', 'FiTrendingDown',
    'FiBarChart', 'FiBarChart2', 'FiPieChart', 'FiActivity',
  ],
  'Tools & Actions': [
    'FiSettings', 'FiTool', 'FiScissors', 'FiFilter', 'FiSearch',
    'FiZoomIn', 'FiZoomOut', 'FiSliders', 'FiEdit', 'FiEdit2',
    'FiTrash', 'FiTrash2', 'FiCopy', 'FiDownload', 'FiUpload',
    'FiRefreshCw', 'FiRotateCw', 'FiSave', 'FiLock', 'FiUnlock', 'FiKey', 'FiShield',
  ],
  'Navigation': [
    'FiArrowUp', 'FiArrowDown', 'FiArrowLeft', 'FiArrowRight',
    'FiChevronUp', 'FiChevronDown', 'FiChevronLeft', 'FiChevronRight',
    'FiChevronsUp', 'FiChevronsDown', 'FiNavigation', 'FiNavigation2',
    'FiCompass', 'FiMap', 'FiMapPin', 'FiMove',
    'FiMaximize', 'FiMaximize2', 'FiMinimize', 'FiMinimize2',
    'FiExternalLink', 'FiCornerUpLeft', 'FiCornerDownRight',
  ],
  'Shapes & Status': [
    'FiCircle', 'FiSquare', 'FiTriangle', 'FiHexagon', 'FiOctagon', 'FiDisc',
    'FiCheckCircle', 'FiXCircle', 'FiAlertCircle', 'FiAlertTriangle',
    'FiInfo', 'FiHelpCircle', 'FiToggleLeft', 'FiToggleRight', 'FiStar', 'FiZap',
  ],
  'Media & Design': [
    'FiImage', 'FiCamera', 'FiCameraOff', 'FiFilm', 'FiMusic', 'FiHeadphones',
    'FiVolume', 'FiVolume2', 'FiVolumeX', 'FiSun', 'FiMoon', 'FiSunrise', 'FiSunset',
    'FiFeather', 'FiPenTool', 'FiCrop', 'FiType', 'FiBold', 'FiItalic',
    'FiAlignLeft', 'FiAlignCenter', 'FiAlignRight',
  ],
}

const RIP_STYLES = `
.rip { position: relative; }

.rip__input {
  width: 100%;
  /* Fixed height, not padding-driven -- matches the 38px this trigger sits directly beside in
     ProjectForm.tsx's Icon/Color form row (that row's Color input is given an explicit
     height: 38 precisely to match its neighboring text inputs; this trigger renders as a third
     "field" in the same row and must match both). Real, reported issue: the old padding-driven
     sizing (10px 12px at 14px font) rendered visibly taller than every other field around it. */
  height: 38px;
  padding: 0 12px;
  font-size: 13px;
  border-radius: 6px;
  cursor: pointer;
  outline: none;
  transition: border-color 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid var(--color-border, #30363d);
  background: var(--color-bg-input, rgba(0, 0, 0, 0.2));
  color: var(--color-text-primary, #e5e7eb);
  box-sizing: border-box;
}
.rip__input:hover { border-color: var(--color-primary, #309B48); }

.rip__selected {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  color: var(--color-primary, #309B48);
}
.rip__selected--empty { color: var(--color-text-secondary, #64748b); }

.rip__label {
  flex: 1;
  font-size: 14px;
  color: var(--color-text-secondary, #94a3b8);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rip__label--has-value { color: var(--color-text-primary, #e5e7eb); }

.rip__arrow { font-size: 12px; color: var(--color-text-secondary, #64748b); }

.rip__dropdown {
  /* position/top/left/width are set inline (see the portal below) -- this is rendered into
     document.body, not into .rip, specifically so a scrollable ancestor (e.g. a Modal body with
     overflow-y: auto -- App Studio's Edit Project modal is exactly this) can never clip it.
     position: absolute relative to .rip was the real, reported bug: once the trigger sat
     more than ~1 dropdown-height above the bottom of a scrolling ancestor, the category tabs
     and lower icon rows rendered outside that ancestor's clip box -- present in the DOM but
     impossible to see or click. */
  position: fixed;
  border-radius: 8px;
  z-index: 10000;
  max-height: 300px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--color-bg-secondary, #0f172a);
  border: 1px solid var(--color-border-accent, rgba(48, 155, 72, 0.3));
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  animation: ripSlideDown 0.15s ease;
}
@keyframes ripSlideDown {
  from { opacity: 0; transform: translateY(-6px); }
  to   { opacity: 1; transform: translateY(0); }
}

.rip__search {
  padding: 12px;
  flex-shrink: 0;
  border-bottom: 1px solid var(--color-border, #30363d);
}
.rip__search-input {
  width: 100%;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 14px;
  outline: none;
  border: 1px solid var(--color-border, #30363d);
  background: var(--color-bg-input, rgba(0, 0, 0, 0.2));
  color: var(--color-text-primary, #e5e7eb);
  box-sizing: border-box;
}
.rip__search-input:focus { border-color: var(--color-primary, #309B48); }

.rip__categories {
  padding: 8px;
  display: flex;
  gap: 4px;
  overflow-x: auto;
  flex-shrink: 0;
  border-bottom: 1px solid var(--color-border, #30363d);
}
.rip__categories::-webkit-scrollbar { height: 3px; }
.rip__categories::-webkit-scrollbar-thumb {
  background: var(--color-border, #30363d);
  border-radius: 2px;
}

.rip__cat {
  padding: 6px 12px;
  font-size: 12px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;
  background: var(--color-surface-muted, rgba(255, 255, 255, 0.05));
  color: var(--color-text-secondary, #94a3b8);
}
.rip__cat--active {
  background: var(--color-primary, #309B48) !important;
  color: #020617 !important;
  font-weight: 600;
}
.rip__cat:hover:not(.rip__cat--active) { opacity: 0.8; }

.rip__grid {
  padding: 12px;
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 6px;
  overflow-y: auto;
  flex: 1;
}

.rip__icon {
  padding: 8px;
  border: 1px solid transparent;
  background: transparent;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-secondary, #94a3b8);
}
.rip__icon:hover {
  background: var(--color-primary-subtle, rgba(48, 155, 72, 0.1));
  color: var(--color-primary, #309B48);
  transform: scale(1.15);
}
.rip__icon--selected {
  background: var(--color-primary-subtle, rgba(48, 155, 72, 0.15)) !important;
  border-color: var(--color-primary, #309B48) !important;
  color: var(--color-primary, #309B48) !important;
}

.rip__empty {
  padding: 24px 12px;
  text-align: center;
  color: var(--color-text-secondary, #94a3b8);
  font-size: 13px;
  grid-column: 1 / -1;
}
`

function formatIconLabel(name: string): string {
  return name.replace(/^Fi/, '').replace(/([A-Z])/g, ' $1').trim()
}

export function renderFiIcon(value: string, size = 18, color = 'currentColor'): React.ReactNode {
  if (!value) return null
  try {
    const Icon = (FiIcons as any)[value] as IconType | undefined
    if (!Icon) {
      console.warn(`Icon not found: ${value}`)
      return null
    }
    return <Icon size={size} color={color} strokeWidth={2} style={{ display: 'flex' }} />
  } catch (err) {
    console.error(`Error rendering icon ${value}:`, err)
    return null
  }
}

export function ReactIconPicker({
  value,
  onChange,
  size = 18,
  placeholder = 'Select icon',
}: ReactIconPickerProps): React.ReactElement {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('Project & Work')
  // Wraps only the trigger now -- the dropdown itself is portaled to document.body (see below),
  // so it needs its own ref for outside-click detection; a click landing inside the portaled
  // dropdown is NOT a descendant of this wrapper in the DOM tree despite being visually "inside"
  // the picker.
  const triggerRef = useRef<HTMLDivElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const [popoverPos, setPopoverPos] = useState<{ top: number; left: number; width: number } | null>(null)

  const updatePopoverPos = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect()
    if (!rect) return
    setPopoverPos({ top: rect.bottom + 4, left: rect.left, width: rect.width })
  }, [])

  useEffect(() => {
    if (!isOpen) return undefined
    updatePopoverPos()
    // Reposition on scroll/resize while open -- the trigger commonly sits inside a scrollable
    // modal body, which the portal's own position: fixed no longer moves with automatically.
    // capture: true so this also fires for scroll on the modal body itself, not just window.
    window.addEventListener('scroll', updatePopoverPos, true)
    window.addEventListener('resize', updatePopoverPos)
    return () => {
      window.removeEventListener('scroll', updatePopoverPos, true)
      window.removeEventListener('resize', updatePopoverPos)
    }
  }, [isOpen, updatePopoverPos])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (triggerRef.current?.contains(target)) return
      if (popoverRef.current?.contains(target)) return
      setIsOpen(false)
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
    return undefined
  }, [isOpen])

  const allIconNames = Object.values(FI_ICON_CATEGORIES).flat()

  const getFilteredIcons = (): Record<string, string[]> => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      const results = allIconNames.filter((n) =>
        formatIconLabel(n).toLowerCase().includes(term)
      )
      return { Results: results }
    }
    return { [selectedCategory]: FI_ICON_CATEGORIES[selectedCategory] ?? [] }
  }

  const handleIconSelect = (iconName: string) => {
    onChange(iconName)
    setIsOpen(false)
    setSearchTerm('')
  }

  const hasValue = !!value
  const triggerLabel = hasValue
    ? value.startsWith('Fi') ? formatIconLabel(value) : value
    : placeholder

  const filteredIcons = getFilteredIcons()
  const visibleIcons = Object.values(filteredIcons).flat()

  return (
    <div className="rip" ref={triggerRef}>
      <div className="rip__input" onClick={() => setIsOpen(!isOpen)}>
        <span className={`rip__selected${hasValue ? '' : ' rip__selected--empty'}`}>
          {hasValue
            ? (() => {
                const Icon = (FiIcons as any)[value] as IconType | undefined
                return Icon ? <Icon size={20} strokeWidth={2} /> : <span style={{ fontSize: '10px' }}>{value.substring(2, 4)}</span>
              })()
            : <FiIcons.FiGrid size={20} />
          }
        </span>
        <span className={`rip__label${hasValue ? ' rip__label--has-value' : ''}`}>
          {triggerLabel}
        </span>
        <span className="rip__arrow">{isOpen ? '▲' : '▼'}</span>
      </div>

      {isOpen && popoverPos && createPortal(
        <div
          className="rip__dropdown"
          ref={popoverRef}
          style={{ top: popoverPos.top, left: popoverPos.left, width: popoverPos.width }}
        >
          <div className="rip__search">
            <input
              type="text"
              placeholder="Search icons..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="rip__search-input"
              autoFocus
            />
          </div>

          {!searchTerm && (
            <div className="rip__categories">
              {Object.keys(FI_ICON_CATEGORIES).map((category) => (
                <button
                  key={category}
                  type="button"
                  className={`rip__cat${category === selectedCategory ? ' rip__cat--active' : ''}`}
                  onClick={() => setSelectedCategory(category)}
                >
                  {category}
                </button>
              ))}
            </div>
          )}

          <div className="rip__grid">
            {visibleIcons.length === 0 ? (
              <div className="rip__empty">No icons found for &ldquo;{searchTerm}&rdquo;</div>
            ) : (
              visibleIcons.map((iconName) => {
                const IconComponent = (FiIcons as any)[iconName] as IconType | undefined
                return (
                  <button
                    key={iconName}
                    type="button"
                    className={`rip__icon${iconName === value ? ' rip__icon--selected' : ''}`}
                    onClick={() => handleIconSelect(iconName)}
                    title={formatIconLabel(iconName)}
                  >
                    {IconComponent ? <IconComponent size={size} strokeWidth={2} /> : <span style={{ fontSize: '11px', fontWeight: 600 }}>{iconName.substring(2, 4)}</span>}
                  </button>
                )
              })
            )}
          </div>
        </div>,
        document.body
      )}

      <style>{RIP_STYLES}</style>
    </div>
  )
}

export default ReactIconPicker
